import Api from '@/services/Api';

export default {
  /**
   * Holt alle SKU-Kategorien vom Backend
   * @returns {Promise} API Response mit Kategorie-Liste
   */
  async getCategories() {
    try {
      return await Api().get('/warehouse/sku-categories');
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
      throw error;
    }
  },

  /**
   * Erstellt eine neue SKU-Kategorie
   * @param {Object} categoryData - Kategorie-Daten
   * @param {string} categoryData.category - Name der Kategorie
   * @param {Array<string>} categoryData.colors - Array mit Farben
   * @param {Array<string>} categoryData.sizes - Array mit Größen
   * @returns {Promise} API Response mit der neuen Kategorie
   */
  async createCategory(categoryData) {
    try {
      return await Api().post('/warehouse/sku-categories', categoryData);
    } catch (error) {
      console.error('Fehler beim Erstellen der Kategorie:', error);
      throw error;
    }
  },

  /**
   * Aktualisiert eine bestehende SKU-Kategorie
   * @param {number} id - ID der Kategorie
   * @param {Object} categoryData - Kategorie-Daten
   * @param {string} categoryData.category - Name der Kategorie
   * @param {Array<string>} categoryData.colors - Array mit Farben
   * @param {Array<string>} categoryData.sizes - Array mit Größen
   * @returns {Promise} API Response mit der aktualisierten Kategorie
   */
  async updateCategory(id, categoryData) {
    try {
      return await Api().put(`/warehouse/sku-categories/${id}`, categoryData);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Kategorie:', error);
      throw error;
    }
  },

  // ===== SKU OPERATIONS =====

  /**
   * Holt alle bestehenden SKUs vom Backend
   * @returns {Promise} API Response mit SKU-Liste
   */
  async getSkus() {
    try {
      return await Api().get('/warehouse/skus');
    } catch (error) {
      console.error('Fehler beim Laden der SKUs:', error);
      throw error;
    }
  },

  /**
   * Erstellt eine neue SKU
   * @param {Object} skuData - SKU Daten
   * @param {number} skuData.categoryId - ID der Kategorie
   * @param {string} skuData.designId - Design ID (optional)
   * @param {Array<string>} skuData.flags - Array von Flags (optional)
   * @returns {Promise} API Response mit der neuen SKU
   */
  async createSku(skuData) {
    try {
      return await Api().post('/warehouse/skus', skuData);
    } catch (error) {
      console.error('Fehler beim Erstellen der SKU:', error);
      throw error;
    }
  },

  /**
   * Erstellt eine freie SKU ohne Design ID (für Typ-Probleme auf Marktplätzen)
   * @param {Object} skuData - SKU Daten
   * @param {string} skuData.sku - SKU Name
   * @param {number} skuData.categoryId - ID der Kategorie
   * @param {Array<string>} skuData.flags - Array von Flags (optional)
   * @returns {Promise} API Response mit der neuen SKU
   */
  async createFreeSku(skuData) {
    try {
      return await Api().post('/warehouse/skus/free', skuData);
    } catch (error) {
      console.error('Fehler beim Erstellen der freien SKU:', error);
      throw error;
    }
  },

  /**
   * Aktualisiert eine bestehende SKU
   * @param {number} id - SKU ID
   * @param {Object} skuData - Aktualisierte SKU Daten
   * @returns {Promise} API Response
   */
  async updateSku(id, skuData) {
    try {
      return await Api().put(`/warehouse/skus/${id}`, skuData);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der SKU:', error);
      throw error;
    }
  },

  /**
   * Löscht eine SKU (hard delete)
   * @param {number} id - SKU ID
   * @returns {Promise} API Response
   */
  async deleteSku(id) {
    try {
      return await Api().delete(`/warehouse/skus/${id}`);
    } catch (error) {
      console.error('Fehler beim Löschen der SKU:', error);
      throw error;
    }
  },

  /**
   * Deaktiviert eine SKU (soft delete)
   * @param {number} id - SKU ID
   * @returns {Promise} API Response
   */
  async deactivateSku(id) {
    try {
      return await Api().patch(`/warehouse/skus/${id}/deactivate`);
    } catch (error) {
      console.error('Fehler beim Deaktivieren der SKU:', error);
      throw error;
    }
  },

  /**
   * Holt Details zu einer spezifischen SKU
   * @param {number} skuId - Die SKU ID
   * @returns {Promise} API Response mit SKU-Details
   */
  async getSkuDetails(skuId) {
    try {
      return await Api().get(`/warehouse/skus/${skuId}`);
    } catch (error) {
      console.error('Fehler beim Laden der SKU-Details:', error);
      throw error;
    }
  },

  // ===== DESIGN ID MANAGEMENT =====

  /**
   * Holt alle Design IDs für eine Kategorie
   * @param {number} categoryId - Kategorie ID
   * @returns {Promise} API Response mit Design ID Liste
   */
  async getDesignIds(categoryId) {
    try {
      return await Api().get(`/warehouse/categories/${categoryId}/design-ids`);
    } catch (error) {
      console.error('Fehler beim Laden der Design IDs:', error);
      throw error;
    }
  },

  /**
   * Analysiert fehlende SKUs für eine Design ID
   * @param {number} categoryId - Kategorie ID
   * @param {string} designId - Design ID (z.B. "D001")
   * @returns {Promise} API Response mit Analyse-Ergebnissen
   */
  async analyzeMissingSkus(categoryId, designId) {
    try {
      return await Api().get(`/warehouse/categories/${categoryId}/design-ids/${designId}/analyze`);
    } catch (error) {
      console.error('Fehler beim Analysieren fehlender SKUs:', error);
      throw error;
    }
  },

  /**
   * Füllt fehlende SKUs für eine Design ID
   * @param {number} categoryId - Kategorie ID
   * @param {string} designId - Design ID (z.B. "D001")
   * @param {Object} options - Optionen
   * @param {Array<string>} options.flags - Flags (optional)
   * @returns {Promise} API Response mit erstellten SKUs
   */
  async fillMissingSkus(categoryId, designId, options = {}) {
    try {
      return await Api().post(`/warehouse/categories/${categoryId}/design-ids/${designId}/fill`, options);
    } catch (error) {
      console.error('Fehler beim Füllen fehlender SKUs:', error);
      throw error;
    }
  },

  // ===== MOCK DATA für Entwicklung =====
  
  /**
   * Mock: Gibt Dummy-Kategorien zurück
   * @returns {Promise} Mock Response mit Kategorie-Liste
   */
  async getCategoriesMock() {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: [
            {
              id: 1,
              name: 'Lineal',
              colors: ['Rot', 'Blau', 'Grün', 'Gelb'],
              sizes: ['15cm', '30cm']
            },
            {
              id: 2,
              name: 'Fruehstuecksbrett',
              colors: ['Natur', 'Dunkelbraun', 'Hellbraun'],
              sizes: ['Klein', 'Mittel', 'Groß']
            },
            {
              id: 3,
              name: 'Trinkflasche',
              colors: ['Silber', 'Schwarz', 'Weiß'],
              sizes: ['500ml', '750ml', '1000ml']
            },
            {
              id: 4,
              name: 'Notizblock',
              colors: ['Weiß', 'Gelb', 'Rosa'],
              sizes: ['A4', 'A5', 'A6']
            }
          ]
        });
      }, 400);
    });
  },

  /**
   * Mock: Erstellt eine neue Kategorie
   * @param {Object} categoryData - Kategorie-Daten
   * @returns {Promise} Mock Response mit neuer Kategorie
   */
  async createCategoryMock(categoryData) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const newCategory = {
          id: Math.floor(Math.random() * 1000) + 100,
          name: categoryData.category,
          colors: categoryData.colors,
          sizes: categoryData.sizes
        };
        
        resolve({ data: newCategory });
      }, 600);
    });
  },

  /**
   * Update SKU candidate status
   */
  async updateSkuCandidateStatus(skuId, candidate) {
    try {
      return await Api().patch(`/warehouse/skus/${skuId}/candidate-status`, { candidate });
    } catch (error) {
      console.error('Fehler beim Aktualisieren des SKU-Kandidaten-Status:', error);
      throw error;
    }
  },

  /**
   * Update SKU category
   */
  async updateSkuCategory(skuId, categoryId) {
    try {
      return await Api().patch(`/warehouse/skus/${skuId}/category`, { categoryId });
    } catch (error) {
      console.error('Fehler beim Aktualisieren der SKU-Kategorie:', error);
      throw error;
    }
  },

  /**
   * Get SKUs by candidate status (candidates, production, or all)
   */
  async getSkusByCandidateStatus(candidateStatus = 'all') {
    try {
      if (candidateStatus === 'all') {
        return await this.getSkus();
      }
      return await Api().get(`/warehouse/skus/status/${candidateStatus}`);
    } catch (error) {
      console.error('Fehler beim Laden der SKUs nach Status:', error);
      throw error;
    }
  },

  // ===== OPTIMIZED SKU ENDPOINTS FOR DESIGN ID VIEW =====

  /**
   * Get SKUs grouped by Design ID (optimized for frontend display)
   * Returns pre-processed data structure with groups and categories
   * @returns {Promise} API Response with { groups: [], categories: [] }
   */
  async getSkusGroupedByDesignId() {
    try {
      return await Api().get('/warehouse/skus-grouped-by-design-id');
    } catch (error) {
      console.error('Fehler beim Laden der gruppierten SKUs:', error);
      throw error;
    }
  },

  /**
   * Get SKUs without Design IDs
   * Returns SKUs that need Design ID assignment
   * @returns {Promise} API Response with SKU array
   */
  async getSkusWithoutDesignId() {
    try {
      return await Api().get('/warehouse/skus-without-design-id');
    } catch (error) {
      console.error('Fehler beim Laden der SKUs ohne Design ID:', error);
      throw error;
    }
  },

  // ===== DESIGN MEDIA OPERATIONS =====

  /**
   * Upload an image for a Design ID
   * @param {string} designId - Design ID
   * @param {File} file - Image file to upload
   * @returns {Promise} API Response with upload result
   */
  async uploadDesignMedia(designId, file) {
    try {
      const formData = new FormData();
      formData.append('image', file);

      return await Api().post(`/warehouse/design-media/${designId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('Fehler beim Hochladen des Design-Bildes:', error);
      throw error;
    }
  },

  /**
   * Get all images for a Design ID
   * @param {string} designId - Design ID
   * @returns {Promise} API Response with image list
   */
  async getDesignMedia(designId) {
    try {
      return await Api().get(`/warehouse/design-media/${designId}`);
    } catch (error) {
      console.error('Fehler beim Laden der Design-Bilder:', error);
      throw error;
    }
  },

  /**
   * Delete a design media image
   * @param {number} imageId - Image ID
   * @returns {Promise} API Response
   */
  async deleteDesignMedia(imageId) {
    try {
      return await Api().delete(`/warehouse/design-media/image/${imageId}`);
    } catch (error) {
      console.error('Fehler beim Löschen des Design-Bildes:', error);
      throw error;
    }
  }
};

